/**
 * VersionTimeline — Version history with rollback buttons + named snapshots
 */

import { RotateCcw, Save, FileText, Camera } from 'lucide-react'
import type { EditorVersionEntry } from '@/lib/api'

interface VersionTimelineProps {
  versions: EditorVersionEntry[]
  onRollback: (versionIndex: number) => void
  currentVersion: number // latest version index
}

function getSourceIcon(source: string) {
  switch (source) {
    case 'original': return <FileText className="w-3 h-3" />
    case 'snapshot': return <Camera className="w-3 h-3" />
    default: return <Save className="w-3 h-3" />
  }
}

export function VersionTimeline({ versions, onRollback }: VersionTimelineProps) {
  if (versions.length === 0) return null

  return (
    <div className="space-y-1">
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-1 mb-2">
        Version History
      </h4>
      <div className="space-y-0.5 max-h-64 overflow-y-auto">
        {versions.map((v, i) => {
          const isLatest = i === versions.length - 1
          return (
            <div
              key={v.version}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${
                isLatest
                  ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'
              }`}
            >
              {/* Timeline dot */}
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                isLatest ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
              }`} />

              {/* Icon */}
              <span className="flex-shrink-0 opacity-60">
                {getSourceIcon(v.source)}
              </span>

              {/* Label */}
              <div className="flex-1 min-w-0 truncate">
                <span className="font-medium">v{v.version}</span>
                <span className="mx-1 opacity-40">&middot;</span>
                <span className="opacity-80">{v.label}</span>
              </div>

              {/* Rollback button (not shown for latest version) */}
              {!isLatest && (
                <button
                  onClick={() => onRollback(v.version)}
                  className="flex-shrink-0 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                  title={`Rollback to v${v.version}`}
                >
                  <RotateCcw className="w-3 h-3" />
                </button>
              )}

              {isLatest && (
                <span className="flex-shrink-0 text-[10px] font-medium text-indigo-500 dark:text-indigo-400">
                  current
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
